import React, { useRef, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Upload, X, Loader2, Image as ImageIcon, FileText } from 'lucide-react';
import { Button } from './ui/button';

/**
 * ImageUpload — uploads a file to /api/uploads and returns the stored URL via onChange.
 * Keeps a URL fallback (existing URLs still render). Used for profile photos & itinerary covers.
 * Props: value (url), onChange(url), kind ('image'|'document'), variant ('avatar'|'cover'),
 *        label, testId, initials (for avatar fallback)
 */
export const ImageUpload = ({ value, onChange, kind = 'image', variant = 'cover',
                              label = 'Image', testId = 'image-upload', initials = '' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const resolved = api.mediaUrl(value);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxMb = kind === 'document' ? 15 : 6;
    if (file.size > maxMb * 1024 * 1024) { toast.error(`File too large (max ${maxMb}MB)`); return; }
    setBusy(true);
    try {
      const { data } = await api.uploadFile(file, kind);
      onChange(data.url);
      toast.success('Uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const isDoc = kind === 'document';

  if (variant === 'avatar') {
    return (
      <div className="flex items-center gap-4">
        <div className="relative h-20 w-20 shrink-0 rounded-full overflow-hidden bg-accent/10 grid place-items-center border border-border">
          {resolved ? (
            <img src={resolved} alt="" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />
          ) : (
            <span className="text-accent font-semibold text-lg">{initials || <ImageIcon className="h-6 w-6" />}</span>
          )}
        </div>
        <div className="space-y-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testId}-input`} />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-xl" disabled={busy}
              onClick={() => inputRef.current?.click()} data-testid={`${testId}-btn`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {value ? 'Replace' : 'Upload photo'}
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" className="rounded-xl text-muted-foreground"
                onClick={() => onChange('')} data-testid={`${testId}-remove`}><X className="h-4 w-4 mr-1" />Remove</Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">JPG, PNG or WebP · max 6MB</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && <p className="text-sm font-medium">{label}</p>}
      <input ref={inputRef} type="file" accept={isDoc ? 'image/*,application/pdf' : 'image/*'} className="hidden" onChange={handleFile} data-testid={`${testId}-input`} />
      {resolved && !isDoc ? (
        <div className="relative rounded-xl overflow-hidden border border-border aspect-video bg-muted">
          <img src={resolved} alt="" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />
          <button type="button" onClick={() => onChange('')} className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-lg bg-background/90 hover:bg-background border border-border" data-testid={`${testId}-remove`}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : resolved && isDoc ? (
        <div className="flex items-center justify-between rounded-xl border border-border p-3">
          <a href={resolved} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-accent hover:underline"><FileText className="h-4 w-4" />View uploaded file</a>
          <button type="button" onClick={() => onChange('')} className="text-muted-foreground hover:text-foreground" data-testid={`${testId}-remove`}><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="w-full rounded-xl border-2 border-dashed border-border hover:border-accent/60 transition-colors py-6 grid place-items-center text-muted-foreground" data-testid={`${testId}-btn`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <span className="flex flex-col items-center gap-1"><Upload className="h-5 w-5" /><span className="text-sm">Click to upload {isDoc ? 'PDF/image' : 'image'}</span></span>
          )}
        </button>
      )}
    </div>
  );
};

export default ImageUpload;
